import React, { useState } from 'react';

type MapLayerKey = 'street' | 'topo' | 'satellite' | 'hillshade';

interface LegendOverlayProps {
  mapLayer?: MapLayerKey;
  defaultOpen?: boolean;
  position?: 'bottom-left' | 'bottom-right';
  embedded?: boolean;  // true = nessun posizionamento assoluto, usato nella toolbar
}

type LegendItem = {
  symbol: string;
  label: string;
};

const viabilitaBase: LegendItem[] = [
  { symbol: '━━━', label: 'Strada principale' },
  { symbol: '───', label: 'Strada secondaria' },
  { symbol: '┄┄┄', label: 'Strada locale' },
  { symbol: '═══', label: 'Pista forestale' },
  { symbol: '⋯⋯⋯', label: 'Sentiero' },
];

const sitacItems: LegendItem[] = [
  { symbol: '🔥', label: 'Perimetro incendio' },
  { symbol: '📍', label: 'Simbolo operativo' },
  { symbol: '✏️', label: 'Vertice modificabile' },
  { symbol: '📡', label: 'Punto GPS acquisito' },
  { symbol: '🧭', label: 'Direzione di provenienza del vento' },
  { symbol: '🟥', label: 'Cono probabile di propagazione' },
  { symbol: '🟨', label: 'Fronte / lato sottovento stimato' },
  { symbol: '🟩', label: 'Retro / lato sopravento più protetto' },
  { symbol: '🟧', label: 'Fianco destro' },
  { symbol: '🟦', label: 'Fianco sinistro' },
];
const toolItems: LegendItem[] = [
  { symbol: '📍', label: 'Inserimento simboli SITAC' },
  { symbol: '🔥', label: 'Modalità perimetro incendio (5C)' },
  { symbol: '📡', label: 'Avvia rilievo GPS' },
  { symbol: '🌬️', label: 'Pannello vento modificabile' },
  { symbol: '⏱️', label: 'Auto-refresh e soglia dato vecchio' },
  { symbol: '⬇️', label: 'Esporta GeoJSON' },
  { symbol: '💾', label: 'Esporta SITAC' },
];

const layerConfig: Record<
  MapLayerKey,
  {
    title: string;
    subtitle: string;
    cartographyItems: LegendItem[];
    note: string;
  }
> = {
  street: {
    title: 'Mappa stradale',
    subtitle: 'Lettura rapida della viabilità e dei riferimenti base',
    cartographyItems: [
      ...viabilitaBase,
      { symbol: '▣', label: 'Area urbanizzata / edificato' },
      { symbol: '●', label: 'Punto di riferimento / località' },
    ],
    note: 'Layer utile per orientamento rapido, accessi e viabilità ordinaria.',
  },
  topo: {
    title: 'Carta topografica',
    subtitle: 'Lettura di viabilità, morfologia e contesto territoriale',
    cartographyItems: [
      ...viabilitaBase,
      { symbol: '≋', label: 'Curve di livello' },
      { symbol: '≈', label: 'Impluvi / corsi d’acqua' },
      { symbol: '▵', label: 'Crinali / rilievi' },
    ],
    note: 'Layer consigliato per lettura del terreno, accessibilità e sviluppo del fronte.',
  },
  satellite: {
    title: 'Immagine satellitare',
    subtitle: 'Lettura visiva del suolo e della copertura reale',
    cartographyItems: [
      { symbol: '🟩', label: 'Vegetazione / bosco' },
      { symbol: '🟫', label: 'Suolo nudo / aree aperte' },
      { symbol: '⬜', label: 'Edificato / superfici artificiali' },
      { symbol: '🟦', label: 'Acqua / invasi / corsi idrici' },
      { symbol: '🛣️', label: 'Tracciati e accessi visibili' },
    ],
    note: 'Layer utile per riconoscimento visivo di vegetazione, aree aperte e ostacoli reali.',
  },
  hillshade: {
    title: 'Rilievo ombreggiato',
    subtitle: 'Lettura del pendio e della forma del terreno',
    cartographyItems: [
      ...viabilitaBase,
      { symbol: '◢', label: 'Versanti ripidi' },
      { symbol: '◣', label: 'Cambi di pendenza / impluvi' },
      { symbol: '▵', label: 'Dossi / rilievi / crinali' },
      { symbol: '⬒', label: 'Aree più dolci / pianori' },
    ],
    note: 'Layer utile per capire esposizione, pendenza e propagazione su terreno complesso.',
  },
};

const layerBadgeConfig: Record<
  MapLayerKey,
  {
    label: string;
    background: string;
    color: string;
    border: string;
  }
> = {
  street: {
    label: '🗺️ Street',
    background: '#1f2937',
    color: '#f9fafb',
    border: '1px solid rgba(255,255,255,0.14)',
  },
  topo: {
    label: '⛰️ Topo',
    background: '#14532d',
    color: '#dcfce7',
    border: '1px solid #166534',
  },
  satellite: {
    label: '🛰️ Satellite',
    background: '#1e3a8a',
    color: '#dbeafe',
    border: '1px solid #2563eb',
  },
  hillshade: {
    label: '🏔️ Hillshade',
    background: '#7c2d12',
    color: '#ffedd5',
    border: '1px solid #ea580c',
  },
};

const LegendSection: React.FC<{ title: string; items: LegendItem[] }> = ({ title, items }) => (
  <div style={{ marginBottom: 12 }}>
    <div
      style={{
        fontSize: '0.8rem',
        fontWeight: 800,
        color: '#fca5a5',
        marginBottom: 6,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
      }}
    >
      {title}
    </div>
    <div style={{ display: 'grid', gap: 6 }}>
      {items.map((item) => (
        <div
          key={`${title}-${item.label}`}
          style={{
            display: 'grid',
            gridTemplateColumns: '72px 1fr',
            gap: 8,
            alignItems: 'center',
            fontSize: '0.83rem',
          }}
        >
          <div style={{ fontWeight: 800, letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
            {item.symbol}
          </div>
          <div>{item.label}</div>
        </div>
      ))}
    </div>
  </div>
);

export const LegendOverlay: React.FC<LegendOverlayProps> = ({
  mapLayer = 'street',
  defaultOpen = false,
  position = 'bottom-right',
  embedded = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  const safeMapLayer: MapLayerKey = layerConfig[mapLayer] ? mapLayer : 'street';
  const currentLayer = layerConfig[safeMapLayer];
  const currentBadge = layerBadgeConfig[safeMapLayer];

  // ── Modalità embedded: solo il contenuto, senza bottone e senza posizionamento assoluto
  if (embedded) {
    return (
      <div style={{ color: '#f9fafb', fontSize: '0.82rem' }}>
        <div style={{ fontWeight: 800, fontSize: '0.88rem', marginBottom: 8, color: '#f97316', letterSpacing: 0.3 }}>
          LEGENDA SITAC
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.76rem', color: '#d1d5db', fontWeight: 700 }}>Layer attivo:</span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '3px 10px', borderRadius: 999,
            background: currentBadge.background, color: currentBadge.color,
            border: currentBadge.border, fontSize: '0.74rem', fontWeight: 800,
          }}>{currentBadge.label}</span>
        </div>
        <div style={{ fontSize: '0.75rem', color: '#d1d5db', marginBottom: 10, lineHeight: 1.4 }}>
          <strong>{currentLayer.title}</strong><br />{currentLayer.subtitle}
        </div>
        <LegendSection title="Cartografia" items={currentLayer.cartographyItems} />
        <LegendSection title="Elementi SITAC" items={sitacItems} />
        <LegendSection title="Strumenti" items={toolItems} />
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.1)',
          fontSize: '0.72rem', lineHeight: 1.4, color: '#94a3b8' }}>
          {currentLayer.note}
        </div>
      </div>
    );
  }

  // ── Modalità overlay sulla mappa (legacy, non più usata ma mantenuta)
  const positionStyle = position === 'bottom-left' ? { left: 10, bottom: 10 } : { right: 10, bottom: 10 };

  return (
    <div
      style={{
        position: 'absolute',
        zIndex: 1200,
        ...positionStyle,
        display: 'flex',
        flexDirection: 'column',
        alignItems: position === 'bottom-left' ? 'flex-start' : 'flex-end',
        gap: 8,
        pointerEvents: 'auto',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          border: '1px solid rgba(255,255,255,0.18)',
          background: '#111827',
          color: '#f9fafb',
          borderRadius: 8,
          padding: '8px 12px',
          fontSize: '0.85rem',
          fontWeight: 700,
          cursor: 'pointer',
          boxShadow: '0 8px 20px rgba(0,0,0,0.28)',
        }}
      >
        {open ? '✖ Legenda' : '📖 Legenda'}
      </button>

      {open && (
        <div
          style={{
            width: 310,
            maxWidth: 'calc(100vw - 24px)',
            background: 'rgba(17, 24, 39, 0.96)',
            color: '#f9fafb',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 12,
            padding: 14,
            boxShadow: '0 10px 28px rgba(0,0,0,0.35)',
            backdropFilter: 'blur(6px)',
          }}
        >
          <div
            style={{
              fontSize: '0.95rem',
              fontWeight: 800,
              marginBottom: 4,
              letterSpacing: 0.2,
            }}
          >
            LEGENDA SITAC
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 10,
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontSize: '0.78rem',
                color: '#d1d5db',
                fontWeight: 700,
              }}
            >
              Layer attivo:
            </span>

            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 999,
                background: currentBadge.background,
                color: currentBadge.color,
                border: currentBadge.border,
                fontSize: '0.76rem',
                fontWeight: 800,
                letterSpacing: 0.2,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
              }}
            >
              {currentBadge.label}
            </span>
          </div>

          <div
            style={{
              fontSize: '0.76rem',
              color: '#d1d5db',
              marginBottom: 12,
              lineHeight: 1.4,
            }}
          >
            <strong>{currentLayer.title}</strong>
            <br />
            {currentLayer.subtitle}
          </div>

          <LegendSection title="Cartografia" items={currentLayer.cartographyItems} />
          <LegendSection title="Elementi SITAC" items={sitacItems} />
          <LegendSection title="Strumenti" items={toolItems} />

          <div
            style={{
              marginTop: 10,
              paddingTop: 10,
              borderTop: '1px solid rgba(255,255,255,0.1)',
              fontSize: '0.74rem',
              lineHeight: 1.4,
              color: '#d1d5db',
            }}
          >
            {currentLayer.note}
          </div>
        </div>
      )}
    </div>
  );
};

export default LegendOverlay;
